import { format, isValid, parse, parseISO } from 'date-fns';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Normalise the shapes a date value can take into a `Date`. A bare
 * `YYYY-MM-DD` string is read as a *local* calendar day (not UTC midnight) so
 * a Postgres `date` column doesn't render as the previous day in negative
 * timezones; everything else goes through `parseISO`. Returns null for empty
 * or unparseable input.
 */
export function parseFlexibleDate(value: Date | string | null | undefined): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  const parsed = DATE_ONLY.test(value) ? parse(value, 'yyyy-MM-dd', new Date()) : parseISO(value);
  return isValid(parsed) ? parsed : null;
}

export interface FormatStandardDateOptions {
  /** Injectable "today" for the current-year check. Defaults to `new Date()`. */
  now?: Date | undefined;
}

/**
 * The one date format: "Mon 28th Jul" when `value` falls in `now`'s calendar
 * year, "Tue 3rd Jan 2027" otherwise. Locale-invariant by design — always
 * this shape, regardless of app locale. Weekday/ordinal/month names come
 * from date-fns' bundled English locale, which is correct for every locale
 * this app supports. Returns '' for null/invalid input.
 */
export function formatStandardDate(
  value: Date | string | null | undefined,
  options?: FormatStandardDateOptions,
): string {
  const date = parseFlexibleDate(value);
  if (!date) return '';
  const now = options?.now ?? new Date();
  const sameYear = date.getFullYear() === now.getFullYear();
  return format(date, sameYear ? 'EEE do MMM' : 'EEE do MMM yyyy');
}

/**
 * Plain ISO calendar day (`yyyy-MM-dd`) for the hover tooltip that shows the
 * unambiguous raw date alongside the standard format — the shape the app
 * displayed before standardisation. Returns null for null/invalid input.
 */
export function isoCalendarDay(value: Date | string | null | undefined): string | null {
  const date = parseFlexibleDate(value);
  return date ? format(date, 'yyyy-MM-dd') : null;
}
